import { HttpException, HttpNotFound } from '@sortlist-frontend/utils';

import { api } from '_core/api/api-sl-base';
import exampleJsonAgency from '_data/api-examples/agency-api/agencies-slug.json';
import { LocaleParams, Service, V3Expertise } from '_types/generic';

export type Agency = typeof exampleJsonAgency;

export type AgencyOffice = {
  address: string;
  position: number;
  agency_slug: string;
  google_place_id?: string | null;
  coordinates: {
    type: 'Point';
    coordinates: [longitude: number, latitude: number];
  };
  location_id: number;
  $location_name: string;
  $iso31661: string;
};

export type AgencyWithRelations = Agency & {
  _relations: {
    offices: AgencyOffice[];
    services: Service[];
  };
};

const urls = {
  // eslint-disable-next-line sonarjs/no-nested-template-literals
  agency: (slugOrUuid: string, locale?: string) => `/core/agencies/${slugOrUuid}${locale ? `?locale=${locale}` : ''}`,
  agencies: (params?: string) => `/core/agencies${params}`,
  agencyServices: (slug: string, locale?: string) =>
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    `/core/agencies/${slug}/services${locale ? `?locale=${locale}` : ''}`,
  agencyExpertises: (slug: string, locale?: string) =>
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    `/core/agencies/${slug}/expertises${locale ? `?locale=${locale}` : ''}`,
  agencyOffices: (agencySlug: string, locale?: string) =>
    // eslint-disable-next-line sonarjs/no-nested-template-literals
    `/core/agencies/${agencySlug}/offices${locale ? `?locale=${locale}` : ''}`,
};

export const agencyRepo = {
  getAgency: async (params: { agencySlugOrUuid: string } & LocaleParams): Promise<Agency> => {
    const { agencySlugOrUuid, locale } = params;
    const response = await api.get(urls.agency(agencySlugOrUuid, locale));
    return response.data;
  },
  /**
   * Return services associated with the agency, by default
   * @see getExpertises
   */
  getServices: async (params: { agencySlug: string } & LocaleParams): Promise<Service[]> => {
    const { agencySlug, locale } = params;
    const response = await api.get(urls.agencyServices(agencySlug, locale));
    return response.data;
  },
  /**
   * Return agency expertises (differs from services)
   * @see getServices
   */
  getExpertises: async (params: { agencySlug: string } & LocaleParams): Promise<V3Expertise[]> => {
    const { agencySlug, locale } = params;
    const response = await api.get(urls.agencyExpertises(agencySlug, locale));
    return response.data;
  },
  getOffices: async (params: { agencySlug: string } & LocaleParams): Promise<AgencyOffice[]> => {
    const { agencySlug, locale } = params;
    const response = await api.get(urls.agencyOffices(agencySlug, locale));
    return response.data;
  },
  /**
   * @throws HttpNotFound|HttpException
   */
  getAgencyWithExpertises: async (params: { agencySlug: string } & LocaleParams) => {
    const errored = {} as {
      agency?: HttpException;
      expertise?: HttpException;
    };
    const { agencySlug: agencySlugOrUuid, ...rest } = params;
    const promises = [
      agencyRepo.getAgency({ agencySlugOrUuid, ...rest }).catch((e) => {
        errored.agency = e;
      }),
      agencyRepo.getExpertises(params).catch((e) => {
        errored.expertise = e;
      }),
    ] as const;

    return Promise.all(promises).then((responses) => {
      const [agency, expertises] = responses;
      // gives precedence to error catched while loading agency
      // over expertises
      if (!agency || !expertises || errored.agency || errored.expertise) {
        throw errored.agency ?? errored.expertise;
      }
      return {
        ...agency,
        ...{ _relations: { expertises } },
      };
    });
  },

  getAgencyWithOfficesAndServices: async (
    params: { agencySlugOrUuid?: string } & LocaleParams,
  ): Promise<AgencyWithRelations | null> => {
    if (!params.agencySlugOrUuid) return null;

    const errored = {} as {
      agency?: HttpException;
      services?: HttpException;
      offices?: HttpException;
    };
    return agencyRepo.getAgency({ ...params, agencySlugOrUuid: params.agencySlugOrUuid as string }).then((agency) => {
      const { slug: agencySlug } = agency;
      const promises = [
        agencyRepo.getOffices({ agencySlug }).catch((e) => {
          errored.offices = e;
        }),

        agencyRepo.getServices({ agencySlug }).catch((e) => {
          errored.services = e;
        }),
      ] as const;

      return Promise.all(promises).then((responses) => {
        const [offices, services] = responses;
        if (!offices || !services || errored.offices || errored.services) {
          throw errored.offices ?? errored.services;
        }

        return {
          ...agency,
          ...{ _relations: { offices, services } },
        };
      });
    });
  },

  /**
   * @throws HttpException if originating exception is !== HttpNotFound
   * @todo optimize with a specific call and better error handling (404)
   */
  exists: async (agencySlugOrUuid: string): Promise<boolean> => {
    try {
      await agencyRepo.getAgency({ agencySlugOrUuid });
    } catch (e) {
      if (e instanceof HttpNotFound) {
        return false;
      }
      throw e;
    }
    return true;
  },
};
